import React, { useEffect, useState, useMemo, useCallback } from 'react';
import '../style/SetMatchDetail.css';
import SetMatchDetailInput from '../modules/SetMatchDetailInput';
import { hasNumberDuplicates, renderSpinner } from '../util/util';
import DatePicker from 'react-datepicker/es';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { et } from 'date-fns/esm/locale';
import { MDBTable, MDBTableBody, MDBTableHead } from 'mdbreact';
import { getTeamId, isUserAdmin, isUserLoggedIn } from './user/User';
import {
  convertDateTimeToIsoString,
  createDateFromBackendDate,
  createDateTimeFromBackendDate,
  getCurrentDateMinusDays,
  getCurrentDateWithZeroTime,
} from '../util/dateUtil';
import {
  fetchMatch,
  fetchLocations,
  fetchHomeTeam,
  fetchAwayTeam,
  fetchSeasonDetails,
  fetchSeasons,
  postMatch,
  patchMatch,
} from '../util/api/requests';
import { Alert } from 'react-bootstrap';
import SubstitutePlayerInput from '../modules/SubstitutePlayerInput';
import Form from 'react-bootstrap/Form';

const GAME_SETS = 4;
const DATEPICKER_TIME_INTERVAL_IN_MINUTES = 15;
const substitution = {
  substitutionGameNo: '',
  teamId: '',
  substitutionEntered: '',
  substitutionPlayer: '',
};

const getExistingGameDetailFields = (data, numberOfGames) => {
  const gameDetailRequests = [];
  for (let game = 0; game < numberOfGames; game++) {
    const setDetailRequests = [];
    for (let set = 0; set < GAME_SETS; set++) {
      setDetailRequests.push({
        awayPlayerId: data.matchDetails[game]?.awayPlayer.playerId ?? 0,
        awaySetClassicPins:
          data.matchDetails[game]?.awayPlayer.sets[set]?.classic ?? 0,
        awaySetMiss: data.matchDetails[game]?.awayPlayer.sets[set]?.miss ?? 0,
        awaySetTotalPins:
          data.matchDetails[game]?.awayPlayer.sets[set]?.total ?? 0,
        awaySubstitutions: [],
        homePlayerId: data.matchDetails[game]?.homePlayer.playerId ?? 0,
        homeSetClassicPins:
          data.matchDetails[game]?.homePlayer.sets[set]?.classic ?? 0,
        homeSetMiss: data.matchDetails[game]?.homePlayer.sets[set]?.miss ?? 0,
        homeSetTotalPins:
          data.matchDetails[game]?.homePlayer.sets[set]?.total ?? 0,
        homeSubstitutions: [],
        setNo: set + 1,
      });
    }
    gameDetailRequests.push({
      gameNo: game + 1,
      setDetailRequests: setDetailRequests,
    });
  }
  return gameDetailRequests;
};

const getNewGameDetailFields = (numberOfGames) => {
  const newGameDetails = Array.from({ length: numberOfGames }, (_, game) => ({
    gameNo: game + 1,
    setDetailRequests: Array.from({ length: GAME_SETS }, (_, set) => ({
      awayPlayerId: '',
      awaySetClassicPins: '',
      awaySetMiss: '',
      awaySetTotalPins: '',
      awaySubstitutions: [],
      homePlayerId: '',
      homeSetClassicPins: '',
      homeSetMiss: '',
      homeSetTotalPins: '',
      homeSubstitutions: [],
      setNo: set + 1,
    })),
  }));
  return newGameDetails;
};

const createSubstitutionObject = (sub) => ({
  substitutionGameNo: sub.substitutionGameNo,
  teamId: sub.teamId,
  substitutionEntered: sub.substitutionEntered,
  substitutionPlayer: sub.substitutionPlayer,
  oldPlayer: sub.substitutionPlayer,
});

const fillWithEmptySubstitutionsUntil = (arr, number, emptySubstitution) => [
  ...arr,
  ...Array.from({ length: Math.max(0, number - arr.length) }, () => ({
    ...emptySubstitution,
  })),
];

const getExistingSubstitutionFields = (data) => {
  const homeSubstitutions = data.substitutions
    .filter((sub) => sub.teamId === data.homeTeamId)
    .map(createSubstitutionObject);

  const awaySubstitutions = data.substitutions
    .filter((sub) => sub.teamId === data.awayTeamId)
    .map(createSubstitutionObject);

  return fillWithEmptySubstitutionsUntil(
    [...homeSubstitutions, ...awaySubstitutions],
    4,
    substitution
  );
};

function AddMatchResult({ mode }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const matchId = searchParams.get('match_id');
  const [gameDetailRequests, setGameDetailRequests] = useState([]);
  const [locationId, setLocationId] = useState('');
  const [locations, setLocations] = useState([]);
  const [matchDateTime, setMatchDateTime] = useState('');
  const [match, setMatch] = useState(null);
  const [isMatchAnnulled, setIsMatchAnnulled] = useState(null);
  const [isForfeit, setIsForfeit] = useState(false);
  const [forfeitingSide, setForfeitingSide] = useState(null); // 'HOME' | 'AWAY'
  const [isSaving, setIsSaving] = useState(false);
  const [homeTeam, setHomeTeam] = useState(null);
  const [awayTeam, setAwayTeam] = useState(null);
  const numberOfGames = homeTeam?.isAllFemaleTeam ? 4 : 6;
  const [errorMessage, setErrorMessage] = useState('');
  const [substitutionRequests, setSubstitutionRequests] = useState(
    Array.from({ length: 4 }, () => ({ ...substitution }))
  );
  const [season, setSeason] = useState(null);
  const [homeSeasonScore, setHomeSeasonScore] = useState(null);
  const [awaySeasonScore, setAwaySeasonScore] = useState(null);

  const setupExistingMatchResult = useCallback((res, loc, numGames) => {
    if (!res || !loc) return;

    const existingLoc = loc.find((l) => l.name === res.location);
    const existingGameDetailFields =
      res.matchDetails?.length > 0
        ? getExistingGameDetailFields(res, numGames)
        : getNewGameDetailFields(numGames);
    const matchDateTime = createDateTimeFromBackendDate(res.matchDateTime);

    setGameDetailRequests(existingGameDetailFields);
    setMatchDateTime(matchDateTime);
    setIsMatchAnnulled(res.isMatchAnnulled);
    setIsForfeit(res.isForfeit || false);
    setForfeitingSide(res.forfeitingSide || null);
    setLocationId(existingLoc?.id);

    if (res.substitutions) {
      const existingSubstitutionFields = getExistingSubstitutionFields(res);
      setSubstitutionRequests(existingSubstitutionFields);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const matchRes = await fetchMatch(matchId);
        setMatch(matchRes);
        if (matchRes) {
          const locations = await fetchLocations();
          const season = await fetchSeasonDetails(matchRes.seasonId);
          const homeTeam = await fetchHomeTeam(matchRes.homeTeamId);
          const awayTeam = await fetchAwayTeam(matchRes.awayTeamId);

          setLocations(locations);
          setSeason(season);
          setHomeTeam(homeTeam);
          setAwayTeam(awayTeam);

          const numGames = homeTeam?.isAllFemaleTeam ? 4 : 6;

          if (mode === 'NEW') {
            const newGameDetails = getNewGameDetailFields(numGames);
            setGameDetailRequests(newGameDetails);
          } else {
            setupExistingMatchResult(matchRes, locations, numGames);
          }

          try {
            const seasonsData = await fetchSeasons();
            if (seasonsData) {
              const seasonEntry = seasonsData.find((s) => s.id === matchRes.seasonId);
              for (const league of seasonEntry?.leagues || []) {
                const leagueTeams = league.teams || [];
                const homeData = leagueTeams.find((t) => t.id === matchRes.homeTeamId);
                const awayData = leagueTeams.find((t) => t.id === matchRes.awayTeamId);
                if (homeData && awayData) {
                  setHomeSeasonScore(homeData.seasonScore || null);
                  setAwaySeasonScore(awayData.seasonScore || null);
                  break;
                }
              }
            }
          } catch (e) {
            console.error('Failed to load season scores:', e);
          }
        }
      } catch (error) {
        console.error('Error initializing match data:', error);
      }
    })();
  }, [mode, matchId]);

  const movedHomePlayers = useMemo(() => {
    if (!homeTeam || !match?.matchDetails) return [];
    return match.matchDetails
      .slice(0, numberOfGames)
      .map((match) => match.homePlayer)
      .filter(
        (player) => !homeTeam.players.some((p) => p.id === player.playerId)
      );
  }, [match, homeTeam, numberOfGames]);

  const movedAwayPlayers = useMemo(() => {
    if (!awayTeam || !match?.matchDetails) return [];
    return match.matchDetails
      .slice(0, numberOfGames)
      .map((match) => match.awayPlayer)
      .filter(
        (player) => !awayTeam.players.some((p) => p.id === player.playerId)
      );
  }, [match, awayTeam, numberOfGames]);

  const movedHomeSubstitutions = useMemo(() => {
    if (!homeTeam || !match?.substitutions) return [];
    const homePlayerIds = new Set(homeTeam.players.map((p) => p.id));
    return match.substitutions.filter(
      (sub) =>
        sub.teamId === match.homeTeamId &&
        !homePlayerIds.has(sub.substitutionPlayer)
    );
  }, [homeTeam, match]);

  const movedAwaySubstitutions = useMemo(() => {
    if (!awayTeam || !match?.substitutions) return [];
    const awayPlayerIds = new Set(awayTeam.players.map((p) => p.id));
    return match.substitutions.filter(
      (sub) =>
        sub.teamId === match.awayTeamId &&
        !awayPlayerIds.has(sub.substitutionPlayer)
    );
  }, [awayTeam, match]);

  const handleChange = (e) => {
    if (
      [
        'awayPlayerId',
        'awaySetClassicPins',
        'awaySetMiss',
        'awaySetTotalPins',
        'awaySubstitutions',
        'homePlayerId',
        'homeSetClassicPins',
        'homeSetMiss',
        'homeSetTotalPins',
        'homeSubstitutions',
        'setNo',
      ].includes(e.target.className)
    ) {
      const gameDetailRequestsArr = [...gameDetailRequests];
      let setDetailRequests =
        gameDetailRequestsArr[e.target.dataset.gameno].setDetailRequests;
      setDetailRequests[e.target.dataset.id][e.target.className] =
        e.target.value.toUpperCase();
      setGameDetailRequests(gameDetailRequestsArr);
    } else if (
      [
        'substitutionPlayer',
        'substitutionEntered',
        'substitutionGameNo',
      ].includes(e.target.className)
    ) {
      const substitutionRequestsArr = [...substitutionRequests];
      substitutionRequestsArr[e.target.dataset.id][e.target.className] =
        e.target.value.toUpperCase();
      substitutionRequestsArr[e.target.dataset.id]['teamId'] =
        e.target.dataset.teamid;
      setSubstitutionRequests(substitutionRequestsArr);
    } else {
      const stateSetters = {
        locationId: setLocationId,
        locations: setLocations,
        matchDateTime: setMatchDateTime,
        isMatchAnnulled: setIsMatchAnnulled,
        isSaving: setIsSaving,
        errorMessage: setErrorMessage,
        substitutionRequests: setSubstitutionRequests,
        season: setSeason,
      };
      if (stateSetters[e.target.name]) {
        stateSetters[e.target.name](e.target.value.toUpperCase());
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrorMessage('');
    if (!matchDateTime) {
      setErrorMessage('Mängu toimumise aeg on kohustuslik väli');
      return;
    }
    if (!locationId) {
      setErrorMessage('Mängu toimumise koht on kohustuslik väli');
      return;
    }
    if (!isForfeit) {
      for (const game of gameDetailRequests) {
        for (const set of game.setDetailRequests) {
          if (
            set.homePlayerId === '' ||
            set.homeSetClassicPins === '' ||
            set.homeSetMiss === '' ||
            set.homeSetTotalPins === '' ||
            set.awayPlayerId === '' ||
            set.awaySetClassicPins === '' ||
            set.awaySetMiss === '' ||
            set.awaySetTotalPins === ''
          ) {
            setErrorMessage('Kõik väljad ei ole täidetud.');
            return;
          }
          if (
            Number(set.homeSetClassicPins) > Number(set.homeSetTotalPins) ||
            Number(set.awaySetClassicPins) > Number(set.awaySetTotalPins)
          ) {
            setErrorMessage(
              'Setis ei saa olla klassika punkte rohkem kui kokku punkte.'
            );
            return;
          }
        }
      }
    }
    for (const sub of substitutionRequests) {
      if (
        (sub.substitutionPlayer !== '' &&
          (sub.substitutionGameNo === '' || sub.substitutionEntered === '')) ||
        (sub.substitutionPlayer === '' &&
          (sub.substitutionGameNo !== '' || sub.substitutionEntered !== ''))
      ) {
        setErrorMessage('Vahetusmängija väljad ei ole täielikult täidetud.');
        return;
      }
    }
    if (isForfeit && !forfeitingSide) {
      setErrorMessage('Vali loobuja võistkond.');
      return;
    }
    if (
      !isForfeit &&
      hasNumberDuplicates(findSelectedPlayersAndSubstitutions())
    ) {
      setErrorMessage('Sama võistleja ei saa osaleda mitmes mängus.');
      return;
    }

    const data = {
      gameDetailRequests: gameDetailRequests,
      locationId: locationId,
      matchDateTime: convertDateTimeToIsoString(matchDateTime),
      isMatchAnnulled: isMatchAnnulled,
      isForfeit: isForfeit,
      forfeitingSide: forfeitingSide,
      matchId: matchId,
      substitutionRequests: substitutionRequests,
    };

    if (mode === 'NEW') {
      saveNewMatch(data);
    } else {
      editMatch(data);
    }
  };

  const saveNewMatch = async (data) => {
    try {
      setIsSaving(true);
      await postMatch(data);
      navigate('/admin');
    } catch (error) {
      if (error.response && error.response.data) {
        setErrorMessage(error.response.data.message);
      }
    }
    setIsSaving(false);
  };

  const editMatch = async (data) => {
    try {
      setIsSaving(true);
      await patchMatch(data);
      navigate('/admin');
    } catch (error) {
      if (error.response && error.response.data) {
        setErrorMessage(error.response.data.message);
      }
    }
    setIsSaving(false);
  };

  const findSelectedPlayersAndSubstitutions = () => {
    const playersAndSubstitutions = [];
    const gameFirstSet = 0;
    gameDetailRequests.forEach((game) => {
      playersAndSubstitutions.push(
        game.setDetailRequests[gameFirstSet].homePlayerId
      );
      playersAndSubstitutions.push(
        game.setDetailRequests[gameFirstSet].awayPlayerId
      );
    });
    substitutionRequests.forEach((substitution) => {
      if (substitution.substitutionPlayer !== '') {
        playersAndSubstitutions.push(substitution.substitutionPlayer);
      }
    });
    return playersAndSubstitutions;
  };

  const setLocation = (e) => {
    setLocationId(e.target.value);
  };

  const conditionallyRenderSaveMatchButton = () => {
    if (isUserAdmin() || isUserWhoIsInHomeOrAwayTeam()) {
      return (
        <input
          type='submit'
          className='btn btn-primary'
          value='Salvesta matš'
        />
      );
    }
    return null;
  };

  const isUserWhoIsInHomeOrAwayTeam = () => {
    if (mode === 'NEW') {
      return (
        isUserLoggedIn() &&
        (getTeamId() === match?.homeTeamId ||
          getTeamId() === match?.awayTeamId) &&
        getCurrentDateWithZeroTime() <=
          createDateFromBackendDate(season.endDate)
      );
    }
    return (
      isUserLoggedIn() &&
      (getTeamId() === match?.homeTeamId ||
        getTeamId() === match?.awayTeamId) &&
      matchDateTime > getCurrentDateMinusDays(5)
    );
  };

  const handleSubstitutionChange = (e) => {
    const { value, dataset } = e.target;
    const { id, teamid } = dataset;

    setSubstitutionRequests((prev) => ({
      ...prev,
      [id]: {
        ...(prev[id] || {}),
        substitutionGameNo: value,
        teamId: teamid,
      },
    }));
  };

  const handleMatchDetailChange = (e, isHome) => {
    const { name, value, dataset } = e.target;
    const idx = Number(dataset.id);
    const isGameHome = isHome ? 'home' : 'away';

    setGameDetailRequests((prev) =>
      prev.map((set, index) =>
        index === idx
          ? {
              ...set,
              [`${isGameHome}${name.replace(isGameHome, '')}`]: value,
            }
          : set
      )
    );
  };

  const renderGames = () => {
    const games = [];

    for (let i = 0; i < numberOfGames; i++) {
      games.push(
        <div key={'gameDiv' + i} className='mt-3'>
          <div className='table_wrapper'>
            <div className='add_home_player'>
              <div className='form-group no_bottom_margin'>
                <MDBTable striped className='merged_tables'>
                  {i === 0 && renderTableHeader('Koduvõistkond')}
                </MDBTable>
              </div>
            </div>
            <div className='add_guest_player'>
              <div className='form-group no_bottom_margin'>
                <MDBTable striped className='merged_tables'>
                  {i === 0 && renderTableHeader('Külalisvõistkond')}
                </MDBTable>
              </div>
            </div>
          </div>

          <div className='table_wrapper'>
            <div className='add_home_player'>
              <SetMatchDetailInput
                setDetailRequests={gameDetailRequests[i].setDetailRequests}
                players={homeTeam?.players}
                movedPlayers={movedHomePlayers}
                gameIndex={i}
                isHome={true}
                onChange={(e) => handleMatchDetailChange(e, true)}
              />
              {renderMatchTotals(i, true, numberOfGames)}
            </div>
            <div className='add_guest_player'>
              <SetMatchDetailInput
                setDetailRequests={gameDetailRequests[i].setDetailRequests}
                players={awayTeam?.players}
                movedPlayers={movedAwayPlayers}
                gameIndex={i}
                isHome={false}
                onChange={(e) => handleMatchDetailChange(e, false)}
              />
              {renderMatchTotals(i, false, numberOfGames)}
            </div>
          </div>
        </div>
      );
    }

    games.push(
      <div
        className='table_wrapper'
        style={{ marginTop: '20px' }}
        key={'substitutions-input-tables'}
      >
        <MDBTable>
          <MDBTableHead color='blue' textWhite>
            <tr>
              <th width='20%'>Mäng nr.</th>
              <th width='60%'>Vahetusmängija</th>
              <th width='20%'>Viske nr.</th>
            </tr>
          </MDBTableHead>
          <MDBTableBody>
            <SubstitutePlayerInput
              players={homeTeam?.players}
              teamId={match?.homeTeamId}
              number='0'
              substitutions={substitutionRequests}
              movedSubstitutions={movedHomeSubstitutions}
              onChange={handleSubstitutionChange}
            />
            <SubstitutePlayerInput
              players={homeTeam?.players}
              teamId={match?.homeTeamId}
              number='1'
              substitutions={substitutionRequests}
              movedSubstitutions={movedHomeSubstitutions}
              onChange={handleSubstitutionChange}
            />
          </MDBTableBody>
        </MDBTable>
        <MDBTable style={{ marginLeft: '10px' }}>
          <MDBTableHead color='blue' textWhite>
            <tr>
              <th width='20%'>Mäng nr.</th>
              <th width='60%'>Vahetusmängija</th>
              <th width='20%'>Viske nr.</th>
            </tr>
          </MDBTableHead>
          <MDBTableBody>
            <SubstitutePlayerInput
              players={awayTeam?.players}
              teamId={match?.awayTeamId}
              number='2'
              substitutions={substitutionRequests}
              movedSubstitutions={movedAwaySubstitutions}
              onChange={handleSubstitutionChange}
            />
            <SubstitutePlayerInput
              players={awayTeam?.players}
              teamId={match?.awayTeamId}
              number='3'
              substitutions={substitutionRequests}
              movedSubstitutions={movedAwaySubstitutions}
              onChange={handleSubstitutionChange}
            />
          </MDBTableBody>
        </MDBTable>
      </div>
    );

    return games;
  };

  const renderMatchTotals = (gameIdx, isHome, numberOfGames) => {
    if (gameIdx === numberOfGames - 1) {
      const matchTotals = calculateMatchTotals(isHome);
      return (
        <MDBTable striped className='merged_tables'>
          <MDBTableBody>
            <tr>
              <td width='120px'>Matš kokku</td>
              <td width='45px'>{matchTotals.classicSum}</td>
              <td width='45px'>{matchTotals.missSum}</td>
              <td width='45px'>{matchTotals.totalSum}</td>
            </tr>
          </MDBTableBody>
        </MDBTable>
      );
    }
  };

  const calculateMatchTotals = (isHome) => {
    let classicSum = 0;
    let missSum = 0;
    let totalSum = 0;
    gameDetailRequests.forEach((game) => {
      game.setDetailRequests.forEach((set) => {
        classicSum += Number(
          isHome ? set.homeSetClassicPins : set.awaySetClassicPins
        );
        missSum += Number(isHome ? set.homeSetMiss : set.awaySetMiss);
        totalSum += Number(
          isHome ? set.homeSetTotalPins : set.awaySetTotalPins
        );
      });
    });
    return { classicSum, missSum, totalSum };
  };

  const renderTableHeader = (teamLabel) => {
    return (
      <MDBTableHead color='blue' textWhite>
        <tr>
          <th width='120px'>{teamLabel}</th>
          <th width='45px'>Klassika</th>
          <th width='45px'>Mööda</th>
          <th width='45px'>Kokku</th>
        </tr>
      </MDBTableHead>
    );
  };

  const renderLocationAndDateInput = () => {
    locations.sort((a, b) => a.name.localeCompare(b.name, 'et'));
    return (
      <div
        className='table_wrapper'
        style={{ flexDirection: 'column', gap: '8px' }}
      >
        <h2>
          {homeTeam?.name} - {awayTeam?.name}
        </h2>
        <MDBTable striped className='merged_tables'>
          <MDBTableHead color='blue' textWhite>
            <tr>
              <th>Asukoht</th>
              <th>Aeg</th>
            </tr>
          </MDBTableHead>
          <MDBTableBody>
            <tr>
              <td style={{ width: '20%' }}>
                <select value={locationId} onChange={(e) => setLocation(e)}>
                  <option value='' />
                  {locations.map((location) => (
                    <option key={'location' + location.id} value={location.id}>
                      {location.name}
                    </option>
                  ))}
                </select>
              </td>
              <td style={{ width: '20%' }}>
                <DatePicker
                  locale={et}
                  selected={matchDateTime}
                  showTimeSelect
                  timeFormat='HH:mm'
                  timeIntervals={DATEPICKER_TIME_INTERVAL_IN_MINUTES}
                  minDate={createDateFromBackendDate(season.startDate)}
                  maxDate={createDateFromBackendDate(season.endDate)}
                  dateFormat='dd.MM.yyyy HH:mm'
                  onChange={(date) => setMatchDateTime(date)}
                />
              </td>
            </tr>
          </MDBTableBody>
        </MDBTable>
        <MDBTable striped className='merged_tables'>
          <MDBTableHead color='blue' textWhite>
            <tr>
              <th>Matš annulleeritud</th>
              <th>Loobumiskaotus</th>
              {isForfeit && <th>Loobuja</th>}
            </tr>
          </MDBTableHead>
          <MDBTableBody>
            <tr>
              <td style={{ width: '15%', textAlign: 'center' }}>
                <Form.Check
                  custom
                  type={`checkbox`}
                  id={`match-annulled-checkbox`}
                  label={``}
                  defaultChecked={isMatchAnnulled}
                  onClick={(event) => setIsMatchAnnulled(event.target.checked)}
                />
              </td>
              <td style={{ width: '15%', textAlign: 'center' }}>
                <Form.Check
                  custom
                  type={`checkbox`}
                  id={`match-forfeit-checkbox`}
                  label={``}
                  checked={isForfeit}
                  onChange={(event) => {
                    setIsForfeit(event.target.checked);
                    if (!event.target.checked) setForfeitingSide(null);
                  }}
                />
              </td>
              {isForfeit && (
                <td style={{ width: '15%' }}>
                  <select
                    value={forfeitingSide || ''}
                    onChange={(e) => setForfeitingSide(e.target.value || null)}
                  >
                    <option value=''>-- vali --</option>
                    <option value='HOME'>
                      {homeTeam?.name}
                      {homeSeasonScore != null &&
                      homeSeasonScore?.forfeitCount > 0
                        ? ` (${homeSeasonScore.forfeitCount}x ${
                            homeSeasonScore.forfeitCount === 1
                              ? 'loobumiskaotus'
                              : 'loobumiskaotust'
                          })`
                        : ''}
                    </option>
                    <option value='AWAY'>
                      {awayTeam?.name}
                      {awaySeasonScore != null &&
                      awaySeasonScore?.forfeitCount > 0
                        ? ` (${awaySeasonScore.forfeitCount}x ${
                            awaySeasonScore.forfeitCount === 1
                              ? 'loobumiskaotus'
                              : 'loobumiskaotust'
                          })`
                        : ''}
                    </option>
                  </select>
                </td>
              )}
            </tr>
          </MDBTableBody>
        </MDBTable>
        {isForfeit &&
          forfeitingSide &&
          (() => {
            const score =
              forfeitingSide === 'HOME' ? homeSeasonScore : awaySeasonScore;
            const team = forfeitingSide === 'HOME' ? homeTeam : awayTeam;
            if (score != null && (score.forfeitCount ?? 0) >= 1) {
              return (
                <Alert
                  variant='warning'
                  className='mt-2 mb-0'
                  style={{ fontSize: '14px' }}
                >
                  <strong>{team?.name}</strong> saab{' '}
                  {(score.forfeitCount ?? 0) + 1}. loobumiskaotuse. Reeglite
                  kohaselt tuleks eemaldada meeskond pärast 2. loobumiskaotust
                  meistrivõistlustelt -{' '}
                  <strong>
                    selle jaoks tuleb meeskond ka käsitsi diskvalifitseerida.
                  </strong>
                </Alert>
              );
            }
            return null;
          })()}
      </div>
    );
  };

  if (gameDetailRequests.length < numberOfGames) {
    return renderSpinner();
  }

  if (
    (!homeTeam?.players.length || !awayTeam?.players.length) &&
    mode === 'NEW'
  ) {
    return (
      <div>
        Võistkonnal puuduvad mängijad ning seega ei ole võimalik mängu lisada
      </div>
    );
  }

  if (isUserLoggedIn()) {
    return (
      <form onSubmit={handleSubmit} onChange={handleChange}>
        {renderLocationAndDateInput()}
        {renderGames()}
        <div className='match_content mt-3'>
          {isSaving ? (
            renderSpinner()
          ) : (
            <>{conditionallyRenderSaveMatchButton()}</>
          )}
        </div>
        {errorMessage && (
          <Alert variant='danger' className={'m-3'}>
            {errorMessage}
          </Alert>
        )}
      </form>
    );
  }

  return <h2>Mängu sisestamiseks peate olema sisse logitud!</h2>;
}

export default AddMatchResult;
